import { Components } from '@/api/generated/api';
import { ICustomTab } from '@/core/elements/CustomTabs.vue';
import { EQuickEntryKind } from '@/composables/quickEntry/quickEntry.enums';
import { EAssessmentVisualizationMode } from '@/interfaces';
import { useLastViewedStore } from '@/stores/useLastViewedStore';
import { storeToRefs } from 'pinia';
import { Ref } from 'vue';

export enum ELastViewedSettingsPage {
  PERFORMANCE_ASSESSMENTS = 'performanceAssessments',
  PERFORMANCE_VISUAL = 'performanceVisual',
  SCHOOL_SUBJECTS = 'schoolSubjects',
  STUDENTS = 'students',
  CLASS_TEAM = 'classTeam',
}

interface ILastViewedComposable {
  getAccordionOpenById: (id: string, clearable: boolean, defaultOpen?: boolean) => boolean;
  lastViewedCompetence: Ref<Components.Schemas.CompetenceDto | null>;
  lastViewedFaqArticle: Ref<Components.Schemas.FaqDto | null>;
  lastViewedAssessmentVisualizationMode: Ref<EAssessmentVisualizationMode>;
  lastViewedReportGroupId: Ref<number | null>;
  lastViewedSchoolLevelFilter: Ref<number>;
  lastViewedSchoolSubjectId: Ref<number | null>;
  lastViewedPerformanceAssessmentId: Ref<number | null>;
  lastUsedQuickEntryType: Ref<EQuickEntryKind | null>;
  lastUsedQuickEntryPerformanceAssessmentId: Ref<number | null>;
  lastViewedSchoolTerm: Ref<Components.Schemas.SchoolTermDto | null>;
  lastViewedSettingsPage: Ref<ELastViewedSettingsPage | null>;
  lastViewedStudentId: Ref<number | null>;
  lastViewedTabPerformanceAssessmentsPageLabel: Ref<string | null>;
  lastViewedTabSkillsPageLabel: Ref<string | null>;
  lastViewedTabStatisticsPageLabel: Ref<string | null>;
  lastViewedTabStudentsPageLabel: Ref<string | null>;
  resetOpenAccordions: () => void;
  setAccordionOpenById: (id: string, open: boolean, clearable: boolean) => void;
  setLastViewedCompetence: (competence: Components.Schemas.CompetenceDto | null) => void;
  setLastViewedFaqArticle: (faqArticle: Components.Schemas.FaqDto | null) => void;
  setLastViewedAssessmentVisualizationMode: (view: EAssessmentVisualizationMode) => void;
  setLastViewedReportGroupId: (reportGroupId: number | null) => void;
  setLastViewedSchoolLevelFilter: (schoolLevel: number) => void;
  setLastViewedSchoolSubjectId: (schoolSubjectId: number | null) => void;
  setLastViewedPerformanceAssessmentId: (performanceAssessmentId: number | null) => void;
  setLastUsedQuickEntryType: (type: EQuickEntryKind, performanceAssessmentId: number | null) => void;
  setLastViewedSchoolTerm: (term: Components.Schemas.SchoolTermDto | null) => void;
  setLastViewedSettingsPage: (page: ELastViewedSettingsPage | null) => void;
  setLastViewedStudentId: (studentId: number | null) => void;
  setLastViewedTabPerformanceAssessmentsPage: (tab: ICustomTab | null) => void;
  setLastViewedTabSkillsPage: (tab: ICustomTab | null) => void;
  setLastViewedTabStatisticsPage: (tab: ICustomTab | null) => void;
  setLastViewedTabStudentsPage: (tab: ICustomTab | null) => void;
}

export const useLastViewed = (): ILastViewedComposable => {
  const lastViewedStore = useLastViewedStore();
  const {
    getAccordionOpenById,
    resetOpenAccordions,
    setAccordionOpenById,
    setLastViewedCompetence,
    setLastViewedFaqArticle,
    setLastViewedAssessmentVisualizationMode,
    setLastViewedReportGroupId,
    setLastViewedSchoolLevelFilter,
    setLastViewedSchoolSubjectId,
    setLastViewedPerformanceAssessmentId,
    setLastUsedQuickEntryType,
    setLastViewedSchoolTerm,
    setLastViewedSettingsPage,
    setLastViewedStudentId,
    setLastViewedTabPerformanceAssessmentsPage,
    setLastViewedTabSkillsPage,
    setLastViewedTabStatisticsPage,
    setLastViewedTabStudentsPage,
  } = lastViewedStore;
  const {
    lastViewedCompetence,
    lastViewedFaqArticle,
    lastViewedAssessmentVisualizationMode,
    lastViewedReportGroupId,
    lastViewedSchoolLevelFilter,
    lastViewedSchoolSubjectId,
    lastViewedPerformanceAssessmentId,
    lastUsedQuickEntryType,
    lastUsedQuickEntryPerformanceAssessmentId,
    lastViewedSchoolTerm,
    lastViewedSettingsPage,
    lastViewedStudentId,
    lastViewedTabPerformanceAssessmentsPageLabel,
    lastViewedTabSkillsPageLabel,
    lastViewedTabStatisticsPageLabel,
    lastViewedTabStudentsPageLabel,
  } = storeToRefs(lastViewedStore);

  return {
    getAccordionOpenById,
    lastViewedCompetence,
    lastViewedFaqArticle,
    lastViewedAssessmentVisualizationMode,
    lastViewedReportGroupId,
    lastViewedSchoolLevelFilter,
    lastViewedSchoolSubjectId,
    lastViewedPerformanceAssessmentId,
    lastUsedQuickEntryType,
    lastUsedQuickEntryPerformanceAssessmentId,
    lastViewedSchoolTerm,
    lastViewedSettingsPage,
    lastViewedStudentId,
    lastViewedTabPerformanceAssessmentsPageLabel,
    lastViewedTabSkillsPageLabel,
    lastViewedTabStatisticsPageLabel,
    lastViewedTabStudentsPageLabel,
    resetOpenAccordions,
    setAccordionOpenById,
    setLastViewedCompetence,
    setLastViewedFaqArticle,
    setLastViewedAssessmentVisualizationMode,
    setLastViewedReportGroupId,
    setLastViewedSchoolLevelFilter,
    setLastViewedSchoolSubjectId,
    setLastViewedPerformanceAssessmentId,
    setLastUsedQuickEntryType,
    setLastViewedSchoolTerm,
    setLastViewedSettingsPage,
    setLastViewedStudentId,
    setLastViewedTabPerformanceAssessmentsPage,
    setLastViewedTabSkillsPage,
    setLastViewedTabStatisticsPage,
    setLastViewedTabStudentsPage,
  };
};
